/**
 * 用户偏好记忆适配器
 * 管理用户的个人偏好、工作模式、习惯等个性化信息
 */

import { MemoryClient } from '../memory-client'
import type {
  MemoryConfig,
  UserPreference,
  PreferenceCategory,
  WorkPattern,
  PersonalizedSuggestion,
  SearchResult
} from '../types'

/**
 * 编码风格偏好
 */
interface CodingStylePreference {
  language: string
  framework?: string
  testingLibrary?: string
  lintingRules?: 'strict' | 'standard' | 'loose'
  codeStyle?: 'functional' | 'oop' | 'mixed'
  namingConvention?: 'camelCase' | 'snake_case' | 'kebab-case'
}

/**
 * UI 偏好
 */
interface UIPreference {
  theme?: 'light' | 'dark' | 'auto'
  density?: 'compact' | 'normal' | 'comfortable'
  language?: string
  fontSize?: number
  colorScheme?: string
}

/**
 * 通知偏好
 */
interface NotificationPreference {
  enabled: boolean
  channels?: ('email' | 'browser' | 'desktop' | 'mobile')[]
  frequency?: 'immediate' | 'hourly' | 'daily' | 'weekly'
  types?: string[]
}

/**
 * 用户偏好适配器
 */
export class UserPreferenceAdapter {
  private memory: MemoryClient
  private userId: string

  constructor(config: MemoryConfig) {
    this.memory = new MemoryClient(config)
    this.userId = config.userId || 'default'
  }

  /**
   * 记录用户偏好
   */
  async recordPreference(params: {
    category: PreferenceCategory
    preference: Record<string, any>
    confidence?: number
  }): Promise<void> {
    const { category, preference, confidence = 0.8 } = params

    // 构建偏好描述
    const description = this.buildPreferenceDescription(category, preference)

    await this.memory.add(description, {
      category: 'user_preference',
      tags: [
        `preference:${category}`,
        ...this.extractPreferenceTags(preference)
      ],
      importance: confidence
    })
  }

  /**
   * 记录工作模式
   */
  async recordWorkPattern(pattern: WorkPattern): Promise<void> {
    const description = [
      '工作模式记录:',
      `高效时段: ${pattern.productiveHours.join(', ')}`,
      `偏好任务类型: ${pattern.preferredTaskTypes.join(', ')}`,
      `休息频率: ${pattern.breakFrequency}`,
      `平均会话时长: ${pattern.averageSessionLength}小时`,
      `偏好复杂度: ${pattern.preferredComplexity}`
    ].join('\n')

    await this.memory.add(description, {
      category: 'work_pattern',
      tags: [
        'work_pattern',
        `complexity:${pattern.preferredComplexity}`,
        ...pattern.preferredTaskTypes.map(type => `task:${type}`)
      ],
      importance: 0.9
    })

    // 记录每个高效时段
    for (const hour of pattern.productiveHours) {
      await this.memory.add(
        `高效工作时段: ${hour}`,
        {
          category: 'work_pattern',
          tags: ['productive_hours', hour],
          importance: 0.8
        }
      )
    }
  }

  /**
   * 获取个性化建议
   */
  async getPersonalizedSuggestions(params: {
    context: string
    currentTime?: string
    recentActivity?: string[]
  }): Promise<PersonalizedSuggestion> {
    const { context, currentTime, recentActivity } = params

    // 根据上下文搜索相关偏好
    const preferences = await this.searchRelevantPreferences(context)
    
    // 如果是任务调度相关，考虑工作模式
    if (context === 'task_scheduling' && currentTime) {
      const workPattern = await this.getWorkPattern()
      return this.generateSchedulingSuggestion(workPattern, currentTime, preferences)
    }

    // 如果是代码相关，考虑编码偏好
    if (context.includes('coding') || context.includes('development')) {
      const codingStyle = await this.getCodingStyle()
      return this.generateCodingSuggestion(codingStyle, preferences)
    }

    // 通用建议
    return this.generateGeneralSuggestion(preferences, recentActivity)
  }

  /**
   * 获取编码风格偏好
   */
  async getCodingStyle(): Promise<CodingStylePreference | null> {
    const results = await this.memory.search('编码风格 语言 框架', {
      filter: {
        category: 'user_preference',
        tags: ['preference:coding_style']
      },
      limit: 10
    })

    if (results.length === 0) return null

    // 合并多个偏好记录
    return this.mergeCodingPreferences(results)
  }

  /**
   * 获取工作模式
   */
  async getWorkPattern(): Promise<WorkPattern | null> {
    const results = await this.memory.search('工作模式记录', {
      filter: {
        category: 'work_pattern'
      },
      limit: 1
    })

    if (results.length === 0) return null

    return this.parseWorkPattern(results[0].memory.content)
  }

  /**
   * 获取 UI 偏好
   */
  async getUIPreferences(): Promise<UIPreference | null> {
    const results = await this.memory.search('UI 界面 主题', {
      filter: {
        category: 'user_preference',
        tags: ['preference:ui_preference']
      },
      limit: 5
    })

    if (results.length === 0) return null

    return this.mergeUIPreferences(results)
  }

  /**
   * 获取通知偏好
   */
  async getNotificationPreferences(): Promise<NotificationPreference | null> {
    const results = await this.memory.search('通知 提醒', {
      filter: {
        category: 'user_preference',
        tags: ['preference:notification']
      },
      limit: 5
    })

    if (results.length === 0) return null

    return this.mergeNotificationPreferences(results)
  }

  /**
   * 学习用户行为
   */
  async learnFromBehavior(params: {
    action: string
    context: Record<string, any>
    timestamp: Date
  }): Promise<void> {
    const { action, context, timestamp } = params

    // 分析行为模式
    const pattern = this.analyzeBehaviorPattern(action, context, timestamp)
    
    if (pattern) {
      await this.memory.add(
        `行为模式: ${action} - ${JSON.stringify(pattern)}`,
        {
          category: 'learning',
          tags: ['behavior', action],
          importance: 0.6
        }
      )
    }
  }

  /**
   * 获取偏好历史
   */
  async getPreferenceHistory(
    category?: PreferenceCategory,
    days: number = 30
  ): Promise<UserPreference[]> {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    const filter: any = {
      category: 'user_preference',
      timeRange: {
        start: startDate,
        end: new Date()
      }
    }

    if (category) {
      filter.tags = [`preference:${category}`]
    }

    const results = await this.memory.search('*', {
      filter,
      limit: 100
    })

    return results.map(result => this.parseUserPreference(result))
  }

  /**
   * 导出用户偏好配置
   */
  async exportPreferences(): Promise<{
    codingStyle?: CodingStylePreference
    workPattern?: WorkPattern
    uiPreferences?: UIPreference
    notificationPreferences?: NotificationPreference
    customPreferences: Record<string, any>
  }> {
    const [codingStyle, workPattern, uiPreferences, notificationPreferences] = 
      await Promise.all([
        this.getCodingStyle(),
        this.getWorkPattern(),
        this.getUIPreferences(),
        this.getNotificationPreferences()
      ])

    // 获取其他自定义偏好
    const customResults = await this.memory.search('偏好', {
      filter: {
        category: 'user_preference'
      },
      limit: 50
    })

    const customPreferences: Record<string, any> = {}
    customResults.forEach(({ memory }) => {
      const category = this.extractPreferenceCategory(memory.content)
      if (category && !['coding_style', 'ui_preference', 'notification'].includes(category)) {
        customPreferences[category] = this.extractPreferenceData(memory.content)
      }
    })

    return {
      codingStyle: codingStyle || undefined,
      workPattern: workPattern || undefined,
      uiPreferences: uiPreferences || undefined,
      notificationPreferences: notificationPreferences || undefined,
      customPreferences
    }
  }

  /**
   * 导入用户偏好
   */
  async importPreferences(preferences: {
    codingStyle?: CodingStylePreference
    workPattern?: WorkPattern
    uiPreferences?: UIPreference
    notificationPreferences?: NotificationPreference
    customPreferences?: Record<string, any>
  }): Promise<void> {
    const promises: Promise<void>[] = []

    if (preferences.codingStyle) {
      promises.push(this.recordPreference({
        category: 'coding_style',
        preference: preferences.codingStyle
      }))
    }

    if (preferences.workPattern) {
      promises.push(this.recordWorkPattern(preferences.workPattern))
    }

    if (preferences.uiPreferences) {
      promises.push(this.recordPreference({
        category: 'ui_preference',
        preference: preferences.uiPreferences
      }))
    }

    if (preferences.notificationPreferences) {
      promises.push(this.recordPreference({
        category: 'notification',
        preference: preferences.notificationPreferences
      }))
    }

    if (preferences.customPreferences) {
      for (const [category, preference] of Object.entries(preferences.customPreferences)) {
        promises.push(this.recordPreference({
          category: category as PreferenceCategory,
          preference
        }))
      }
    }

    await Promise.all(promises)
  }

  // ============= 私有方法 =============

  /**
   * 构建偏好描述
   */
  private buildPreferenceDescription(
    category: PreferenceCategory,
    preference: Record<string, any>
  ): string {
    const categoryLabels: Record<PreferenceCategory, string> = {
      coding_style: '编码风格偏好',
      work_pattern: '工作模式偏好',
      tool_preference: '工具偏好',
      communication: '沟通偏好',
      ui_preference: 'UI界面偏好',
      notification: '通知偏好',
      other: '其他偏好'
    }

    const lines = [`${categoryLabels[category]}:`]
    
    for (const [key, value] of Object.entries(preference)) {
      lines.push(`${key}: ${JSON.stringify(value)}`)
    }

    return lines.join('\n')
  }

  /**
   * 提取偏好标签
   */
  private extractPreferenceTags(preference: Record<string, any>): string[] {
    const tags: string[] = []

    if (preference.language) tags.push(`lang:${preference.language}`)
    if (preference.framework) tags.push(`framework:${preference.framework}`)
    if (preference.theme) tags.push(`theme:${preference.theme}`)
    if (preference.enabled !== undefined) tags.push(`enabled:${preference.enabled}`)

    return tags
  }

  /**
   * 搜索相关偏好
   */
  private async searchRelevantPreferences(context: string): Promise<SearchResult[]> {
    return this.memory.search(context, {
      filter: {
        category: ['user_preference', 'work_pattern']
      },
      limit: 20
    })
  }

  /**
   * 生成调度建议
   */
  private generateSchedulingSuggestion(
    workPattern: WorkPattern | null,
    currentTime: string,
    preferences: SearchResult[]
  ): PersonalizedSuggestion {
    if (!workPattern) {
      return {
        recommendation: '建议先记录您的工作模式，以便提供更准确的建议',
        context: 'task_scheduling',
        confidence: 0.3,
        basedOn: []
      }
    }

    // 检查当前时间是否在高效时段
    const isProductiveHour = workPattern.productiveHours.some(hour => 
      currentTime.includes(hour.split('-')[0])
    )

    if (isProductiveHour) {
      return {
        recommendation: `现在是您的高效时段，建议处理${workPattern.preferredComplexity}复杂度的${workPattern.preferredTaskTypes.join('或')}任务`,
        context: 'task_scheduling',
        confidence: 0.9,
        basedOn: preferences.map(p => p.memory.id),
        alternativeOptions: [
          '如果感到疲劳，可以处理一些简单任务',
          `记得${workPattern.breakFrequency}`
        ]
      }
    }

    return {
      recommendation: '当前不是您的高效时段，建议处理一些简单的维护性任务或文档工作',
      context: 'task_scheduling',
      confidence: 0.8,
      basedOn: preferences.map(p => p.memory.id),
      alternativeOptions: [
        '也可以利用这段时间进行学习和规划',
        '或者适当休息，为高效时段做准备'
      ]
    }
  }

  /**
   * 生成编码建议
   */
  private generateCodingSuggestion(
    codingStyle: CodingStylePreference | null,
    preferences: SearchResult[]
  ): PersonalizedSuggestion {
    if (!codingStyle) {
      return {
        recommendation: '建议使用 TypeScript 和现代化的开发工具',
        context: 'coding',
        confidence: 0.4,
        basedOn: []
      }
    }

    const recommendations = []
    
    if (codingStyle.language) {
      recommendations.push(`使用 ${codingStyle.language} 进行开发`)
    }
    if (codingStyle.framework) {
      recommendations.push(`采用 ${codingStyle.framework} 框架`)
    }
    if (codingStyle.testingLibrary) {
      recommendations.push(`使用 ${codingStyle.testingLibrary} 进行测试`)
    }
    if (codingStyle.lintingRules) {
      recommendations.push(`应用 ${codingStyle.lintingRules} 级别的代码规范`)
    }

    return {
      recommendation: recommendations.join('，'),
      context: 'coding',
      confidence: 0.85,
      basedOn: preferences.map(p => p.memory.id)
    }
  }

  /**
   * 生成通用建议
   */
  private generateGeneralSuggestion(
    preferences: SearchResult[],
    recentActivity?: string[]
  ): PersonalizedSuggestion {
    if (preferences.length === 0) {
      return {
        recommendation: '开始使用系统，我会学习您的偏好并提供个性化建议',
        context: 'general',
        confidence: 0.2,
        basedOn: []
      }
    }

    // 分析最相关的偏好
    const topPreference = preferences[0]
    
    return {
      recommendation: `基于您的使用习惯，${this.extractRecommendation(topPreference.memory.content)}`,
      context: 'general',
      confidence: topPreference.score,
      basedOn: [topPreference.memory.id]
    }
  }

  /**
   * 合并编码偏好
   */
  private mergeCodingPreferences(results: SearchResult[]): CodingStylePreference {
    const merged: CodingStylePreference = {
      language: 'TypeScript' // 默认值
    }

    results.forEach(({ memory }) => {
      const prefs = this.extractPreferenceData(memory.content)
      Object.assign(merged, prefs)
    })

    return merged
  }

  /**
   * 解析工作模式
   */
  private parseWorkPattern(content: string): WorkPattern {
    const lines = content.split('\n')
    const pattern: Partial<WorkPattern> = {}

    lines.forEach(line => {
      if (line.includes('高效时段:')) {
        pattern.productiveHours = line.split(':')[1].trim().split(', ')
      } else if (line.includes('偏好任务类型:')) {
        pattern.preferredTaskTypes = line.split(':')[1].trim().split(', ')
      } else if (line.includes('休息频率:')) {
        pattern.breakFrequency = line.split(':')[1].trim()
      } else if (line.includes('平均会话时长:')) {
        pattern.averageSessionLength = parseFloat(line.match(/(\d+)/)?.[1] || '2')
      } else if (line.includes('偏好复杂度:')) {
        pattern.preferredComplexity = line.split(':')[1].trim() as any
      }
    })

    return pattern as WorkPattern
  }

  /**
   * 合并 UI 偏好
   */
  private mergeUIPreferences(results: SearchResult[]): UIPreference {
    const merged: UIPreference = {}

    results.forEach(({ memory }) => {
      const prefs = this.extractPreferenceData(memory.content)
      Object.assign(merged, prefs)
    })

    return merged
  }

  /**
   * 合并通知偏好
   */
  private mergeNotificationPreferences(results: SearchResult[]): NotificationPreference {
    const merged: NotificationPreference = {
      enabled: true
    }

    results.forEach(({ memory }) => {
      const prefs = this.extractPreferenceData(memory.content)
      Object.assign(merged, prefs)
    })

    return merged
  }

  /**
   * 分析行为模式
   */
  private analyzeBehaviorPattern(
    action: string,
    context: Record<string, any>,
    timestamp: Date
  ): Record<string, any> | null {
    const hour = timestamp.getHours()
    
    // 分析时间模式
    if (action.includes('task_complete')) {
      return {
        productiveHour: `${hour}:00-${hour + 1}:00`,
        taskType: context.taskType,
        duration: context.duration
      }
    }

    // 分析工具使用
    if (action.includes('tool_use')) {
      return {
        tool: context.tool,
        frequency: context.frequency,
        context: context.useCase
      }
    }

    return null
  }

  /**
   * 解析用户偏好
   */
  private parseUserPreference(result: SearchResult): UserPreference {
    const { memory } = result
    const category = this.extractPreferenceCategory(memory.content) || 'other'
    const preference = this.extractPreferenceData(memory.content)

    return {
      category,
      preference,
      confidence: result.score,
      learnedAt: memory.createdAt,
      lastUpdated: memory.updatedAt
    }
  }

  /**
   * 提取偏好分类
   */
  private extractPreferenceCategory(content: string): PreferenceCategory | null {
    const match = content.match(/(\w+)偏好:/)
    if (!match) return null

    const categoryMap: Record<string, PreferenceCategory> = {
      '编码风格': 'coding_style',
      '工作模式': 'work_pattern',
      '工具': 'tool_preference',
      '沟通': 'communication',
      'UI界面': 'ui_preference',
      '通知': 'notification'
    }

    return categoryMap[match[1]] || 'other'
  }

  /**
   * 提取偏好数据
   */
  private extractPreferenceData(content: string): Record<string, any> {
    const data: Record<string, any> = {}
    const lines = content.split('\n').slice(1) // 跳过标题行

    lines.forEach(line => {
      const [key, value] = line.split(':').map(s => s.trim())
      if (key && value) {
        try {
          data[key] = JSON.parse(value)
        } catch {
          data[key] = value
        }
      }
    })

    return data
  }

  /**
   * 提取推荐内容
   */
  private extractRecommendation(content: string): string {
    // 简单提取第一行作为推荐
    return content.split('\n')[0].replace(/.*偏好:/, '').trim()
  }
}